import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import translationNO from './locales/no.json';
import translationEN from './locales/en.json';

// Iter 761: appens i18n-koder er `no` | `en`. Backend/kundemodellen bruker `nb` | `en` —
// `toUiLocale()` er den ENE mappingen, så kundespråk nb alltid blir portal-locale no.
export const UI_LANGS = ['no', 'en'] as const;
export type UiLang = (typeof UI_LANGS)[number];

export const toUiLocale = (lang?: string | null): UiLang | null => {
  const v = (lang || '').toLowerCase();
  if (!v) return null;
  if (v === 'en' || v.startsWith('en-') || v.startsWith('en_')) return 'en';
  if (v === 'no' || v === 'nb' || v === 'nn' || v.startsWith('nb-') || v.startsWith('nb_') || v.startsWith('no-')) return 'no';
  return null;
};

const resources = {
  no: { translation: translationNO },
  en: { translation: translationEN },
};

// Lagret språk fra localStorage, ellers NORSK (standard — ingen endring før noen velger engelsk).
const getSavedLanguage = (): UiLang => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const userLang = toUiLocale(localStorage.getItem('user_preferred_language'));
      if (userLang) return userLang;
      const saved = toUiLocale(localStorage.getItem('i18nextLng'));
      if (saved) return saved;
    }
  } catch {
    /* localStorage utilgjengelig */
  }
  return 'no';
};

// Mangler nøkkelen i valgt språk → vis det ANDRE språket; mangler den i begge → lesbar tekst fra nøkkelen (aldri «customers.title»).
const humanizeKey = (key: string) => {
  const last = key.split('.').pop() || key;
  const words = last.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(),
    fallbackLng: (code?: string) => (toUiLocale(code) === 'en' ? ['no'] : ['en']),
    defaultNS: 'translation',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    returnEmptyString: false,
    parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue || humanizeKey(key),
    saveMissing: true,
    missingKeyHandler: (_lngs: readonly string[], _ns: string, key: string) => {
      if (typeof window === 'undefined') return;
      const w = window as any;
      (w.__i18nMissing ||= new Set<string>()).add(key);
    },
  });

// Iter 763: offentlige sider (sporing, reiseplan, katalog …) bytter språk per lenke/kunde uten å
// overskrive den innloggede brukerens preferanse i samme nettleser.
let persistMode: 'app' | 'public' = 'app';
export const setLanguagePersistMode = (mode: 'app' | 'public') => { persistMode = mode; };
export const getAppLanguage = (): UiLang => getSavedLanguage();

i18n.on('languageChanged', (lng: string) => {
  if (typeof window !== 'undefined' && window.localStorage) {
    if (persistMode === 'app') {
      localStorage.setItem('user_preferred_language', lng);
      localStorage.setItem('i18nextLng', lng);
    }
    document.documentElement.lang = lng === 'no' ? 'nb' : lng;
  }
});

export default i18n;
